import { useEffect, useState } from "react";
import {
  getInpatientTableScroll,
  getReceptionTableScroll,
} from "../utils/receptionTableScroll";

const FILL_SHELL_SELECTOR =
  ".ui-table-fill-shell, .reception-patient-list-shell";

const BOTTOM_PAD = 12;

const getFallbackScroll = (isMobile, variant) => {
  if (variant === "inpatient") {
    return getInpatientTableScroll(isMobile);
  }
  if (variant === "services") {
    return getReceptionTableScroll({
      isMobile,
      extraChrome: isMobile ? 100 : 80,
    });
  }
  return getReceptionTableScroll({ isMobile });
};

const clearListScroll = (anchor) => {
  anchor.querySelectorAll(".ant-list-items").forEach((el) => {
    el.style.maxHeight = "";
    el.style.overflowY = "";
  });
  anchor.classList.remove("ui-list-body-scroll");
  anchor.style.removeProperty("--ui-list-scroll-y");
};

const applyListScroll = (anchor, maxBody) => {
  const listItems = anchor.querySelector(".ant-list-items");
  if (!listItems) return false;

  listItems.style.maxHeight = `${maxBody}px`;
  listItems.style.overflowY = "auto";
  anchor.style.setProperty("--ui-list-scroll-y", `${maxBody}px`);
  anchor.classList.add("ui-list-body-scroll");
  return true;
};

const measureMaxBody = (anchor) => {
  const listItems = anchor.querySelector(".ant-list-items");
  const pagination =
    anchor.querySelector(".ant-table-pagination") ||
    anchor.querySelector(".ant-list-pagination");
  const paginationHeight = pagination?.getBoundingClientRect().height ?? 0;

  if (listItems) {
    const top = listItems.getBoundingClientRect().top;
    return Math.max(
      Math.floor(window.innerHeight - top - BOTTOM_PAD - paginationHeight),
      120
    );
  }

  const thead =
    anchor.querySelector(".ant-table-thead") ||
    anchor.querySelector(".ant-table-header");
  const theadHeight = thead?.getBoundingClientRect().height ?? 55;
  const top = anchor.getBoundingClientRect().top;
  return Math.max(
    Math.floor(
      window.innerHeight - top - BOTTOM_PAD - theadHeight - paginationHeight
    ),
    120
  );
};

const measureContentHeight = (anchor) => {
  const tbody = anchor.querySelector(".ant-table-tbody");
  if (tbody?.scrollHeight) return tbody.scrollHeight;

  const listItems = anchor.querySelector(".ant-list-items");
  if (listItems?.scrollHeight) return listItems.scrollHeight;

  return 0;
};

const usePageFillTableScroll = (
  containerRef,
  isMobile = false,
  variant = "default",
  refreshKey = 0
) => {
  const [scroll, setScroll] = useState({ x: "max-content" });

  useEffect(() => {
    const update = () => {
      const node = containerRef.current;
      if (!node) {
        setScroll({ x: "max-content" });
        return;
      }

      const anchor = node.closest(FILL_SHELL_SELECTOR) || node;
      const top = anchor.getBoundingClientRect().top;
      if (top <= 0) {
        clearListScroll(anchor);
        setScroll(getFallbackScroll(isMobile, variant));
        return;
      }

      const maxBody = measureMaxBody(anchor);
      const contentHeight = measureContentHeight(anchor);
      const hasList = Boolean(anchor.querySelector(".ant-list-items"));
      const overflows = contentHeight > maxBody;

      if (overflows) {
        if (hasList) applyListScroll(anchor, maxBody);
        setScroll({ x: "max-content", y: maxBody });
      } else {
        clearListScroll(anchor);
        setScroll({ x: "max-content" });
      }
    };

    update();
    const frameId = window.requestAnimationFrame(update);
    const timeoutId = window.setTimeout(update, 50);
    const timeoutId2 = window.setTimeout(update, 300);

    const node = containerRef.current;
    const resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(update);
    });
    const mutationObserver = new MutationObserver(() => {
      window.requestAnimationFrame(update);
    });

    if (node) {
      resizeObserver.observe(node);
      mutationObserver.observe(node, { childList: true, subtree: true });
      const shell = node.closest(FILL_SHELL_SELECTOR);
      if (shell && shell !== node) resizeObserver.observe(shell);
    }

    window.addEventListener("resize", update);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
      window.clearTimeout(timeoutId2);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", update);
      if (node) {
        const anchor = node.closest(FILL_SHELL_SELECTOR) || node;
        clearListScroll(anchor);
      }
    };
  }, [containerRef, isMobile, variant, refreshKey]);

  return scroll;
};

export default usePageFillTableScroll;
